import React, { Component } from "react";
import AppContext from "../../context/app-context";
import { ReactComponent as DropDownArrow } from "../../../assets/images/down-chevron.svg";
import CategoryListEntry from "../category-list-entry/category-list-entry.component";
import {
  MapCategory,
  MapFilter,
} from "../../../interfaces/map/common.interfaces";
import { FeatureType } from "../../../enums/map.enums";
import styles from "./dropdown.module.css";
import { postMessage } from "../../../utils/map.utils";

type DropdownProps = {};
type DropdownState = {
  isOpen: boolean;
  selectedCategoryIds: Set<string>;
  originalMapFilters?: MapFilter[];
};

class Dropdown extends Component<DropdownProps, DropdownState> {
  static contextType = AppContext;
  context!: React.ContextType<typeof AppContext>;

  // Track the last context values we've seen to detect changes
  private lastSyncedMapFilters: MapFilter[] | undefined = undefined;
  private lastSyncedFocusedPaths: MapFilter[] | undefined = undefined;
  // Set true when clearFocusedCategoryPaths fires (external navigation signal)
  private pendingExternalSync = false;

  constructor(props: DropdownProps) {
    super(props);

    this.state = {
      isOpen: false,
      selectedCategoryIds: new Set(),
      originalMapFilters: [],
    };
  }

  detectSelectedCategories(mapFilters: MapFilter[] | undefined): Set<string> {
    const { mapPaths, mapSnowmobileSubPaths, mapPathCategories } = this.context;
    const selectedIds = new Set<string>();
    if (!mapFilters || mapFilters.length === 0) return selectedIds;

    const pathIds = new Set(
      mapFilters.filter((f) => f.type === FeatureType.Path).flatMap((f) => f.ids)
    );
    const snowmobileIds = new Set(
      mapFilters.filter((f) => f.type === FeatureType.SnowmobileSubPath).flatMap((f) => f.ids)
    );
    Object.values(mapPathCategories).forEach((category) => {
      if (category.id === "2") {
        const hasSnowmobileSubPathFilter = Object.values(mapSnowmobileSubPaths).some((p) => snowmobileIds.has(p.id));
        const hasSnowmobilePathFilter = mapFilters.some(
          (f) => f.type === FeatureType.SnowmobilePath || f.type === FeatureType.SnowmobileFra
        );
        const hasPathFilter = Object.values(mapPaths).some((p) => p.pathCategoryId === "2" && pathIds.has(p.id));
        if (hasSnowmobileSubPathFilter || hasSnowmobilePathFilter || hasPathFilter) {
          selectedIds.add("2");
        }
      } else {
        if (Object.values(mapPaths).some((p) => p.pathCategoryId === category.id && pathIds.has(p.id))) {
          selectedIds.add(category.id);
        }
      }
    });
    return selectedIds;
  }

  buildAllPathsFilters(): MapFilter[] {
    const { mapPaths, mapSnowmobileSubPaths } = this.context;
    const filters: MapFilter[] = [];
    const allPathIds = Object.values(mapPaths).map((p) => p.id);
    const allSnowmobileIds = Object.values(mapSnowmobileSubPaths).map((p) => p.id);
    if (allPathIds.length > 0) {
      filters.push({ type: FeatureType.Path, ids: allPathIds });
    }
    if (allSnowmobileIds.length > 0) {
      filters.push({ type: FeatureType.SnowmobileSubPath, ids: allSnowmobileIds });
    }
    return filters;
  }

  componentDidUpdate(): void {
    const { mapFilters, focusedCategoryPaths } = this.context;

    // Detect clearFocusedCategoryPaths — the signal that external navigation is about to set new filters.
    // External nav always clears focused paths first, then calls setFilters.
    // The dropdown always sets focused paths to a non-empty value when applying filters.
    if (focusedCategoryPaths !== this.lastSyncedFocusedPaths) {
      this.lastSyncedFocusedPaths = focusedCategoryPaths;
      if (!focusedCategoryPaths || focusedCategoryPaths.length === 0) {
        this.pendingExternalSync = true;
      } else {
        this.pendingExternalSync = false;
      }
    }

    if (mapFilters !== this.lastSyncedMapFilters) {
      this.lastSyncedMapFilters = mapFilters;
      if (this.pendingExternalSync) {
        this.pendingExternalSync = false;
        this.setState({
          originalMapFilters: mapFilters,
          selectedCategoryIds: this.detectSelectedCategories(mapFilters),
        });
      }
    }
  }

  applyFilters = (selectedIds: Set<string>) => {
    const {
      mapPaths,
      mapSnowmobileSubPaths,
      setFocusedCategoryPaths,
      setHighlighted,
      setSelected,
      setMapFilters,
    } = this.context;

    if (selectedIds.size === 0) {
      const allFilters = this.buildAllPathsFilters();
      setTimeout(() => {
        setFocusedCategoryPaths(allFilters);
        setMapFilters(allFilters);
        setTimeout(() => {
          setSelected(undefined);
          setHighlighted(undefined);
          postMessage(JSON.stringify({ deselect: true }));
        });
      });
      return;
    }

    const snowmobileIds: string[] = [];
    const regularIds: string[] = [];

    selectedIds.forEach((catId) => {
      if (catId === "2") {
        snowmobileIds.push(
          ...Object.values(mapSnowmobileSubPaths).map((p) => p.id)
        );
      } else {
        regularIds.push(
          ...Object.values(mapPaths)
            .filter((p) => p.pathCategoryId === catId)
            .map((p) => p.id)
        );
      }
    });

    const filters: MapFilter[] = [];
    if (snowmobileIds.length > 0) {
      filters.push({ type: FeatureType.SnowmobileSubPath, ids: snowmobileIds });
    }
    if (regularIds.length > 0) {
      filters.push({ type: FeatureType.Path, ids: regularIds });
    }

    setTimeout(() => {
      setFocusedCategoryPaths(filters);
      setMapFilters(filters);
      setTimeout(() => {
        setSelected(undefined);
        setHighlighted(undefined);
        postMessage(JSON.stringify({ deselect: true }));
      });
    });
  };

  toggleCategory = (category: MapCategory) => {
    const next = new Set(this.state.selectedCategoryIds);
    if (next.has(category.id)) {
      next.delete(category.id);
    } else {
      next.add(category.id);
    }
    this.setState({ selectedCategoryIds: next });
    this.applyFilters(next);
  };

  setAllCategoryPaths = () => {
    const { setFocusedCategoryPaths, setMapFilters, setHighlighted, setSelected } = this.context;
    const allFilters = this.buildAllPathsFilters();

    setTimeout(() => {
      setFocusedCategoryPaths(allFilters);
      setMapFilters(allFilters);
      setTimeout(() => {
        setSelected();
        setHighlighted();
      });
    });

    this.setState({ selectedCategoryIds: new Set() });
  };

  toggleOpen = () => {
    this.setState({ isOpen: !this.state.isOpen });
  };

  render() {
    const { mapPathCategories, mapFilters, useEnglish } = this.context;
    const { isOpen, selectedCategoryIds } = this.state;
    const language = useEnglish ? "en" : "sv";
    const defaultTitle = useEnglish ? "Show all paths" : "Visa alla leder";

    const hasActiveNonPathFilter =
      !!mapFilters &&
      mapFilters.length > 0 &&
      !mapFilters.some(
        (f) =>
          f.type === FeatureType.Path ||
          f.type === FeatureType.SnowmobileSubPath ||
          f.type === FeatureType.SnowmobilePath ||
          f.type === FeatureType.SnowmobileFra
      );

    const count = selectedCategoryIds.size;
    const singleCategory =
      count === 1
        ? Object.values(mapPathCategories).find((c) => selectedCategoryIds.has(c.id))
        : undefined;
    const label =
      count === 0
        ? defaultTitle
        : singleCategory
        ? (useEnglish ? singleCategory.pageTitle_en : singleCategory.pageTitle) ?? singleCategory.pageTitle
        : useEnglish
        ? `${count} categories selected`
        : `${count} kategorier valda`;

    const defaultCategory: MapCategory = {
      id: "0",
      identifier: "default",
      pageTitle: "Visa alla leder",
      pageTitle_en: "Show all paths",
    };

    return (
      <div className={styles.dropdown}>
        <div className={styles.menuContainer} onClick={() => this.toggleOpen()}>
          <p className={styles.text}>{label}</p>
          <DropDownArrow
            className={isOpen ? styles.chevronOpen : styles.chevron}
          />
        </div>

        <div className={styles.dropdownItems}>
          {isOpen && (
            <div>
              {Object.values(mapPathCategories).map((category: MapCategory) => (
                <CategoryListEntry
                  key={category.id}
                  category={category}
                  isActive={selectedCategoryIds.has(category.id)}
                  onClick={() => this.toggleCategory(category)}
                  language={language}
                />
              ))}
              <CategoryListEntry
                category={defaultCategory}
                isActive={!hasActiveNonPathFilter && selectedCategoryIds.size === 0}
                onClick={() => this.setAllCategoryPaths()}
                language={language}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default Dropdown;
